import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  LinearProgress,
  Chip,
  Grid,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip
} from '@mui/material';
import {
  Star,
  Rocket,
  Business,
  Upgrade
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import EnhancedPricingPage from './EnhancedPricingPage';
import { isSubscriptionManagementDisabled, getSubscriptionDisabledReason } from '../utils/paymentConfig';

const SubscriptionCard = () => {
  const { API_URL } = require('../utils/apiConfig');
    
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPricing, setShowPricing] = useState(false);
  const [error, setError] = useState('');
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cpraStatus, setCpraStatus] = useState({ success: null, message: '' });
  
  const { user, token } = useAuth();

  useEffect(() => {
    if (user) {
      loadSubscriptionData();
    }
  }, [user]);

  const loadSubscriptionData = async () => {
    try {
      setLoading(true);
      const cacheBuster = Date.now();
      
      // Load current subscription
      const subResponse = await fetch(`/api/subscriptions/current?_t=${cacheBuster}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });
      const subData = await subResponse.json();
      
      if (subData.success) {
        setSubscription(subData.subscription);
      }
      
      // Load usage data
      const usageResponse = await fetch(`/api/subscriptions/usage?_t=${cacheBuster}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });
      const usageData = await usageResponse.json();
      
      if (usageData.success) {
        setUsage(usageData.usage);
      }
      
    } catch (error) {
      console.error('Error loading subscription data:', error);
      setError('Failed to load subscription information');
    } finally {
      setLoading(false);
    }
  };

  const handleCpraOptOut = async () => {
    try {
      const res = await fetch('/api/privacy/do-not-sell', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (data.success) {
        setCpraStatus({ success: true, message: data.message || 'Your opt-out preference has been recorded.' });
      } else {
        setCpraStatus({ success: false, message: data.error || 'Failed to record opt-out. Please try again.' });
      }
    } catch (error) {
      setCpraStatus({ success: false, message: 'Network error. Please try again later.' });
    }
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    setError('');

    try {
      const response = await fetch('/api/subscriptions/cancel', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setShowCancelDialog(false);
        // Reload subscription data to show updated status
        await loadSubscriptionData();
      } else {
        setError(data.error || 'Failed to cancel subscription');
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error);
      setError('Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  const getPlanIcon = (planName) => {
    switch (planName?.toLowerCase()) {
      case 'free':
        return <Star sx={{ fontSize: 24, color: 'primary.main' }} />;
      case 'premium':
        return <Rocket sx={{ fontSize: 24, color: 'secondary.main' }} />;
      case 'enterprise':
        return <Business sx={{ fontSize: 24, color: 'success.main' }} />;
      default:
        return <Star sx={{ fontSize: 24, color: 'primary.main' }} />;
    }
  };

  const getPlanColor = (planName) => {
    switch (planName?.toLowerCase()) {
      case 'free':
        return 'primary';
      case 'premium':
        return 'secondary';
      case 'enterprise':
        return 'success';
      default:
        return 'primary';
    }
  };

  const getUsagePercentage = (current, limit) => {
    if (limit === -1) return 0; // Unlimited
    return Math.min((current / limit) * 100, 100);
  };

  const getUsageColor = (percentage) => {
    if (percentage >= 90) return 'error';
    if (percentage >= 70) return 'warning';
    return 'success';
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardContent>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {getPlanIcon(subscription?.planName)}
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {subscription?.planName || 'Free'} Plan
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Current Subscription
                </Typography>
              </Box>
            </Box>
            <Chip 
              label="FREE" 
              color="success" 
              sx={{ fontWeight: 'bold' }}
            />
          </Box>

          {/* Usage Statistics */}
          {usage && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                This Month's Usage
              </Typography>
              
              <Grid container spacing={2}>
                {/* Interviews Usage */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">Interviews</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {usage.current.interviewsThisMonth} / {usage.limits.interviewsPerMonth === -1 ? '∞' : usage.limits.interviewsPerMonth}
                      </Typography>
                    </Box>
                    {usage.limits.interviewsPerMonth !== -1 && (
                      <LinearProgress 
                        variant="determinate" 
                        value={getUsagePercentage(usage.current.interviewsThisMonth, usage.limits.interviewsPerMonth)}
                        color={getUsageColor(getUsagePercentage(usage.current.interviewsThisMonth, usage.limits.interviewsPerMonth))}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    )}
                    {usage.limits.interviewsPerMonth === -1 && (
                      <Box sx={{ textAlign: 'center', py: 1 }}>
                        <Chip label="Unlimited" size="small" color="success" />
                      </Box>
                    )}
                  </Box>
                </Grid>

                {/* Resume Uploads Usage */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">Resume Uploads</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {usage.current.resumeUploadsThisMonth} / {usage.limits.resumeUploads === -1 ? '∞' : usage.limits.resumeUploads}
                      </Typography>
                    </Box>
                    {usage.limits.resumeUploads !== -1 && (
                      <LinearProgress 
                        variant="determinate" 
                        value={getUsagePercentage(usage.current.resumeUploadsThisMonth, usage.limits.resumeUploads)}
                        color={getUsageColor(getUsagePercentage(usage.current.resumeUploadsThisMonth, usage.limits.resumeUploads))}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                    )}
                    {usage.limits.resumeUploads === -1 && (
                      <Box sx={{ textAlign: 'center', py: 1 }}>
                        <Chip label="Unlimited" size="small" color="success" />
                      </Box>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Plan Features */}
          {subscription?.features && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                Your Plan Includes:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {Object.entries(subscription.features)
                  .filter(([key, value]) => value === true)
                  .map(([key]) => key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()))
                  .slice(0, 3)
                  .map((feature, index) => (
                    <Chip 
                      key={index}
                      label={feature}
                      size="small"
                      color={getPlanColor(subscription.planName)}
                      variant="outlined"
                    />
                  ))}
                {Object.entries(subscription.features).filter(([key, value]) => value === true).length > 3 && (
                  <Chip 
                    label={`+${Object.entries(subscription.features).filter(([key, value]) => value === true).length - 3} more`}
                    size="small"
                    variant="outlined"
                  />
                )}
              </Box>
            </Box>
          )}

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Tooltip 
              title={isSubscriptionManagementDisabled() ? getSubscriptionDisabledReason() : ''}
              arrow
            >
              <span>
                <Button
                  variant="contained"
                  startIcon={<Upgrade />}
                  onClick={() => setShowPricing(true)}
                  color={getPlanColor(subscription?.planName)}
                  disabled={isSubscriptionManagementDisabled()}
                  sx={{
                    ...(isSubscriptionManagementDisabled() && {
                      opacity: 0.6,
                      cursor: 'not-allowed'
                    })
                  }}
                >
                  {subscription?.planName === 'Free' ? 'Upgrade Plan' : 'Change Plan'}
                </Button>
              </span>
            </Tooltip>
            
            {subscription?.planName !== 'Free' && (
              <Tooltip 
                title={isSubscriptionManagementDisabled() ? getSubscriptionDisabledReason() : ''}
                arrow
              >
                <span>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => setShowCancelDialog(true)}
                    disabled={isSubscriptionManagementDisabled()}
                    sx={{
                      ...(isSubscriptionManagementDisabled() && {
                        opacity: 0.6,
                        cursor: 'not-allowed'
                      })
                    }}
                  >
                    Cancel Subscription
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>

          {/* CPRA Opt-Out */}
          <Box sx={{ mt: 2 }}>
            <Button
              variant="text"
              color="inherit"
              onClick={handleCpraOptOut}
              sx={{ px: 0, textTransform: 'none' }}
            >
              Do Not Sell My Personal Information
            </Button>
            {cpraStatus.success !== null && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color={cpraStatus.success ? 'success.main' : 'error.main'}>
                  {cpraStatus.message}
                </Typography>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Pricing Dialog */}
      <Dialog 
        open={showPricing} 
        onClose={() => setShowPricing(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Choose Your Plan</Typography>
            <Button onClick={() => setShowPricing(false)}>×</Button>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <EnhancedPricingPage onClose={() => {
            setShowPricing(false);
            loadSubscriptionData(); // Reload data after subscription change
          }} />
        </DialogContent>
      </Dialog>

      {/* Cancel Subscription Dialog */}
      <Dialog
        open={showCancelDialog}
        onClose={() => setShowCancelDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cancel Subscription</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to cancel your subscription? You'll lose access to premium features at the end of your current billing period.
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setShowCancelDialog(false)}
            disabled={cancelling}
          >
            Keep Subscription
          </Button>
          <Button 
            onClick={handleCancelSubscription}
            color="error"
            variant="contained"
            disabled={cancelling}
            startIcon={cancelling ? <CircularProgress size={16} /> : null}
          >
            {cancelling ? 'Cancelling...' : 'Cancel Subscription'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SubscriptionCard;